import { useStores } from '@littlebird/sdk';
import { buildRoutineRunChatPrompt } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import CopyButton from '@/components/blocks/CopyButton';
import { IconButton } from '@/components/ui';
import { useOpenChat } from '@/features/chat/use-open-chat';
import { useTabs } from '@/features/tabs';
import { logError } from '@/lib/errorLogging';
import { trackEvent } from '@/utils/analytics';

import { routinesStore } from '../routines.state';

export const RoutineReportCopyButton = ({ text }: { text: string }) => (
  <CopyButton
    text={text}
    copyText="Copy"
    icon={Icons.copy}
    size="sm"
    disabled={!text}
    aria-label="Copy report"
  />
);

export const RoutineReportSendToChat = observer(
  ({ routineId }: { routineId: number }) => {
    const selectedReportId = routinesStore.useSelectedReportId(routineId);
    const {
      threads: threadStore,
      reports: reportStore,
      routines: routineStore,
    } = useStores();
    const { openTab } = useTabs();
    const { openChat, isOpening } = useOpenChat();
    const [pending, setPending] = useState(false);

    const selectedRoutine = routineStore.getByReferenceId(routineId);
    const currentReport = reportStore.orderedReports.find(
      (report) => report.referenceId === selectedReportId
    );

    const handleConvertToChat = async () => {
      if (!currentReport || !selectedRoutine) return;

      try {
        setPending(true);
        if (currentReport.chatId) {
          trackEvent('REPORT_REVEAL_CHAT', {
            reportId: currentReport.referenceId,
          });
          await openChat(currentReport.chatId);
          return;
        }

        trackEvent('REPORT_TO_CHAT', { reportId: currentReport.referenceId });
        const userMessageWithTimePeriod = buildRoutineRunChatPrompt(
          selectedRoutine.prompt,
          selectedRoutine.recurrenceRule,
          currentReport.scheduledAt
        );
        const fullReport = await reportStore.fetchByReferenceId(
          currentReport.referenceId
        );
        if (!fullReport) throw new Error('Could not load the full report');
        const threadId = await threadStore.createThreadFromRoutine({
          title: currentReport.title,
          userMessage: userMessageWithTimePeriod,
          assistantMessage: fullReport.text,
          origin: 'routine',
        });
        openTab({ path: `/chats/${threadId}` });
      } catch (error) {
        logError(error, 'Failed to convert report to chat', {
          context: 'ReportView',
        });
        toast.error('Failed to send note to chat', {
          description:
            error instanceof Error ? error.message : 'Please try again',
        });
      } finally {
        setPending(false);
      }
    };

    return (
      <IconButton
        size="sm"
        icon={Icons.sendToChat}
        onClick={handleConvertToChat}
        disabled={!currentReport || pending || isOpening}
        isLoading={pending || isOpening}
        tooltip="Send to chat"
        aria-label="Send report to chat"
      />
    );
  }
);
